import type { MonitoringHit, SanctionSource, SearchHit, SourceNote } from './types';

export const getResultHit = (hit: MonitoringHit | SearchHit) => {
	// Instant search
	if ('doc' in hit) {
		const readAdhocSearchSource = (sourceIndex: string, source: SourceNote) => {
			return {
				name: source?.name,
				url: source?.url,
				listing_started_utc: source?.listing_started_utc,
				listing_ended_utc: source?.listing_ended_utc,
				list: hit.doc.fields?.length
					? hit.doc?.fields
							.filter((field) => field.source === sourceIndex)
							.map((field) => ({
								name: field.name,
								value: field.value,
								isURL: field.tag === 'related_url',
							}))
					: [],
			};
		};

		const buildList = (amlType: string) => {
			return Object.entries(hit.doc.source_notes ?? {})
				.filter(([_sourceIndex, source]) => source.aml_types?.[0] === amlType)
				.map(([sourceIndex, source]) => readAdhocSearchSource(sourceIndex, source));
		};

		const listUniqueDates = (tag: string) => {
			const uniqueDates = new Set<string>(
				hit.doc.fields?.length ? hit.doc.fields.filter((field) => field.tag === tag).map((field) => field.value) : [],
			);
			return uniqueDates.size ? Array.from(uniqueDates).join(', ') : 'N/A';
		};

		return {
			id: hit.doc.id,
			keyInfo: {
				name: hit.doc.name,
				entityType: hit.doc.entity_type,
				aka: hit.doc.aka?.length ? hit.doc.aka.map(({ name }) => name).join(', ') : 'N/A',
				countries: hit.doc.fields?.length
					? hit.doc.fields
							.filter((field) => field.tag === 'country_names' && field.name === 'Countries')
							.map((field) => field.value)
							.join(', ')
					: 'N/A',
				dateOfBirth: listUniqueDates('date_of_birth'),
				dateOfDeath: listUniqueDates('date_of_death'),
				associates: hit.doc.associates?.length
					? hit.doc.associates.map(({ name, association }) => `${name} (${association})`).join(', ')
					: 'N/A',
			},
			sanctionList: buildList('sanction'),
			warningList: buildList('warning'),
			fitnessProbityList: buildList('fitness-probity'),
			pepList: buildList('pep'),
			pepClass1List: buildList('pep-class-1'),
			pepClass2List: buildList('pep-class-2'),
			pepClass3List: buildList('pep-class-3'),
			pepClass4List: buildList('pep-class-4'),
			adverseMedia: hit.doc.media?.length
				? hit.doc.media.map((media) => {
						return {
							title: media?.title,
							url: media?.url,
							date: media?.date,
							snippet: media?.snippet,
						};
					})
				: [],
		};
	}

	// Monitoring
	//  https://docs.complyadvantage.com/#entities

	const readMonitoringSource = (source: SanctionSource) => {
		return {
			name: source?.name,
			url: source?.url,
			listing_started_utc: source?.listing_started_utc,
			listing_ended_utc: source?.listing_ended_utc,
			list: source.data
				? source.data
						.filter((data) => data.source === source.source)
						.map((data) => ({
							name: data.name,
							value: data.value,
							isURL: data.tag === 'related_url',
						}))
				: [],
		};
	};

	return {
		id: hit.id,
		keyInfo: {
			name: hit.key_information?.name,
			entityType: hit.key_information?.entity_type,
			aka: hit.key_information?.aka?.map(({ name }) => name).join(', ') || 'N/A',
			countries: hit.key_information?.country_names,
			dateOfBirth: hit.key_information?.date_of_birth,
			otherDateOfBirths: hit.key_information?.dates_of_birth?.join(', '),
			dateOfDeath: hit.key_information?.date_of_death,
			associates: hit.key_information?.associates?.length
				? hit.key_information.associates.map(({ name, association }) => `${name} (${association})`).join(', ')
				: 'N/A',
		},
		sanctionList: Object.values(hit.full_listing?.sanction || {}).map(readMonitoringSource),
		warningList: Object.values(hit.full_listing?.warning || {}).map(readMonitoringSource),
		fitnessProbityList: Object.values(hit.full_listing?.['fitness-probity'] || {}).map(readMonitoringSource),
		pepList: Object.values(hit.full_listing?.pep || {}).map(readMonitoringSource),
		pepClass1List: Object.values(hit.full_listing?.['pep-class-1'] || {}).map(readMonitoringSource),
		pepClass2List: Object.values(hit.full_listing?.['pep-class-2'] || {}).map(readMonitoringSource),
		pepClass3List: Object.values(hit.full_listing?.['pep-class-3'] || {}).map(readMonitoringSource),
		pepClass4List: Object.values(hit.full_listing?.['pep-class-4'] || {}).map(readMonitoringSource),
		adverseMedia: hit.uncategorized?.media
			? Object.values(hit.uncategorized?.media).map((media) => ({
					title: media?.title,
					url: media?.url,
					date: media?.date,
					snippet: media?.snippet,
				}))
			: [],
	};
};
